/**
 * main.ts — точка входа: инициализация приложения
 * Проект: TorgiRT (shared — все витрины)
 *
 * Адаптация rd/app.js для витрины /i/:
 * 1. Каталог загружается из PHP API (CatalogApiController): /i/ajax/catalog
 *    Путь берётся из window.CART_CONFIG.catalogApiUrl (задаётся footer.php)
 * 2. Цены уже приходят с тенантским множителем (markup_i) от сервера — JS НЕ домножает
 * 3. Изображения приходят как полные пути (/magimg/…) — менять не нужно
 * 4. Корзина: localStorage + серверная синхронизация через /i/ajax/cart/sync
 * 5. DEMO_PRODUCTS убраны — каталог полностью из БД
 */

import { state, refs, productLookup, ITEMS_PER_PAGE } from './state';
import {
  loadCatalogData, registerProductLookup, fetchProducts,
} from './catalog';
import { runCatalogFetchThen } from './catalog-loading';
import { setButtonBusy } from './button-busy';
import { normalizeProduct, getConfig, isCatalogListingPage, isOnZakazPage } from './helpers';
import { loadCartFromStorage, syncCartToServer, clearCart } from './cart';
import { mergeFavoritesWithServer } from './favorites';
import { restoreStateFromUrl, pushUrlState } from './url';
import { restoreSpaProductScroll } from './spa-back-scroll';
import {
  render, renderModal, updateModalImage, renderProductsCount, renderProducts, renderPagination,
  showToast, updateFavButton,
} from './render';
import { handleClick, handleChange, handleKeydown, initListRowTouchHighlight, initAjaxGallery } from './events';
import { applySearch, handleSearchInput, initSearchHints, initSearchOverlay } from './search';
import { closeProductModal, shiftModalImage } from './modal';
import { initFloatingCartScroll } from './floating-cart';
import { submitAuthForm, validateLoginForm } from './auth';
import { initDrawer } from './drawer';
import { initHoldRepeat } from './hold-repeat';
import { initQtyKeyboard } from './qty-keyboard';
import { ICONS } from './icons';
import { mountStorefrontFlashApi, showFlash } from './toast';

mountStorefrontFlashApi();

/* ── Init ── */

async function init(): Promise<void> {
  await loadCatalogData();
  registerProductLookup();

  loadCartFromStorage();

  if (getConfig('isAuthenticated', false)) {
    await mergeFavoritesWithServer();
  }

  document.querySelectorAll('.fav-btn[data-article]').forEach(btn => {
    updateFavButton((btn as HTMLElement).dataset.article!);
  });

  if (window.__PRODUCT_PAGE_DATA__) {
    const raw = window.__PRODUCT_PAGE_DATA__;
    const ppd = normalizeProduct(raw) as any;
    productLookup.set(ppd.article, ppd);
    ppd.characteristics = (raw as any).characteristics || {};
    state.productPage = ppd;
    state.productPageImageIndex = 0;
    delete window.__PRODUCT_PAGE_DATA__;
  }

  if (state.cart.length > 0) {
    void syncCartToServer();
  }

  restoreStateFromUrl();

  if (state.productPage) {
    delete window.__INITIAL_PRODUCTS__;
  } else {
    if (window.__INITIAL_PRODUCTS__ && Array.isArray(window.__INITIAL_PRODUCTS__.products)) {
      const data = window.__INITIAL_PRODUCTS__;
      state.products = data.products.map(normalizeProduct);
      state.pagination = {
        total:      data.total ?? 0,
        page:       data.page ?? 1,
        perPage:    data.per_page ?? ITEMS_PER_PAGE,
        totalPages: data.total_pages ?? 1,
      };
      state.currentPage = state.pagination.page;
      state.filterValues = data.filter_values ?? {};

      for (const product of state.products) {
        productLookup.set(product.article, product);
      }

      delete window.__INITIAL_PRODUCTS__;

      const ssrSection = document.querySelector('#products[data-ssr]');
      if (ssrSection && state.viewMode === 'grid') {
        state.ssrHydrated = true;
      }
    } else {
      delete window.__INITIAL_PRODUCTS__;
      await fetchProducts();
    }
  }

  if (isOnZakazPage() && state.cart.length > 0) {
    state.cartOpen = true;
  }

  if (isOnZakazPage() && refs.cartItems) {
    const anchor = document.getElementById("zakaz-cart-anchor");
    if (anchor) {
      anchor.appendChild(refs.cartItems);
      /* /zakaz: без .container — состав встраивается в ритм страницы заказа */
      refs.cartItems.classList.remove("container");
    }
    refs.cartItemsWrap?.classList.add("hidden");
  }

  if (refs.searchInput) {
    (refs.searchInput as HTMLInputElement).value = state.searchQuery;
  }

  if (refs.productsCount) {
    refs.productsCount.addEventListener("animationend", () => {
      refs.productsCount!.classList.remove("is-count-changed");
    });
  }

  document.addEventListener("click", handleClick);
  document.addEventListener("keydown", handleKeydown);
  document.addEventListener("change", handleChange);

  let _priceInputTimer: ReturnType<typeof setTimeout>   = 0;
  document.addEventListener("input", (event: Event) => {
    const target = event.target as HTMLInputElement;
    const action = target.dataset.action;
    if (action !== "set-price-min" && action !== "set-price-max") return;

    if (action === "set-price-min") {
      state.priceMin = target.value;
    } else {
      state.priceMax = target.value;
    }

    clearTimeout(_priceInputTimer);
    _priceInputTimer = setTimeout(() => {
      void runCatalogFetchThen(() => fetchProducts({ page: 1 }), () => {
        pushUrlState();
        renderProductsCount();
        renderProducts();
        renderPagination();
      });
    }, 300);
  });

  document.addEventListener('submit', (event: Event) => {
    const form = (event.target as HTMLElement | null)?.closest?.('form.history-chat-form');
    if (!form || !(form instanceof HTMLFormElement)) return;
    const btn = form.querySelector('button[type="submit"]');
    if (btn instanceof HTMLButtonElement) {
      setButtonBusy(btn, true);
    }
  });

  if (refs.searchInput) {
    refs.searchInput.addEventListener("input", handleSearchInput);
    refs.searchInput.addEventListener("keydown", (event: Event) => {
      if ((event as KeyboardEvent).key === "Enter") {
        event.preventDefault();
        applySearch(true);
      }
    });
    const searchForm = refs.searchInput.closest("form.header-search-wrap");
    searchForm?.addEventListener("submit", (event: Event) => {
      if (!isCatalogListingPage()) return;
      event.preventDefault();
      applySearch(true);
    });
  }
  document.addEventListener("keydown", (event: KeyboardEvent) => {
    if (!state.selectedProduct) return;

    if (event.key === "Escape") {
      if (state.imageViewerOpen) {
        state.imageViewerOpen = false;
      } else {
        closeProductModal();
      }
      renderModal();
      return;
    }

    if (state.imageViewerOpen) {
      if (event.key === "ArrowLeft") {
        shiftModalImage(-1);
        updateModalImage();
        return;
      }
      if (event.key === "ArrowRight") {
        shiftModalImage(1);
        updateModalImage();
        return;
      }
    }
  });

  window.addEventListener("popstate", () => void (async () => {
    if (window.location.pathname.includes("/product/")) {
      const match = window.location.pathname.match(/\/product\/([^/]+)/);
      if (match) {
        const article = decodeURIComponent(match[1]);
        const cached = productLookup.get(article);
        if (cached) {
          state.productPage = cached as any;
          state.productPageImageIndex = 0;
          render();
          window.scrollTo(0, 0);
          return;
        }
        const jsonUrl = getConfig("ajaxProductJsonUrl", "/i/ajax/product/json") as string;
        try {
          const res = await fetch(jsonUrl + "?article=" + encodeURIComponent(article));
          if (!res.ok) throw new Error();
          const data = await res.json();
          const product = normalizeProduct(data) as any;
          product.characteristics = data.characteristics || {};
          productLookup.set(product.article, product);
          state.productPage = product;
          state.productPageImageIndex = 0;
          render();
          window.scrollTo(0, 0);
          return;
        } catch {
          window.location.reload();
          return;
        }
      }
      window.location.reload();
      return;
    }

    state.productPage = null;
    state.productPageImageIndex = 0;
    state.selectedCategory = null;
    state.selectedSubcategory = null;
    state.breadcrumbPath = null;
    state.currentFilters = [];
    state.activeFilters = {};
    state.searchMode = "strict";
    state.currentPage = 1;
    state.searchQuery = "";
    state.priceMin = "";
    state.priceMax = "";
    state.stockOnly = false;
    state.favoritesOnly = false;
    state.sortBy = "";
    if (refs.searchInput) (refs.searchInput as HTMLInputElement).value = "";
    restoreStateFromUrl();
    if (refs.searchInput) (refs.searchInput as HTMLInputElement).value = state.searchQuery;
    await runCatalogFetchThen(() => fetchProducts(), () => {
      render();
      restoreSpaProductScroll();
    });
  })());

  // H.5: заполнить SVG-иконки bottom-nav
  document.querySelectorAll('.bottom-nav-icon[data-icon]').forEach(el => {
    const iconName = (el as HTMLElement).getAttribute('data-icon') ?? '';
    const iconMap: Record<string, string> = { catalog: 'grid', search: 'search', favorites: 'heart', cart: 'cart' };
    const key = iconMap[iconName] ?? iconName;
    if (ICONS[key]) (el as HTMLElement).innerHTML = ICONS[key];
  });

  // H.1: инициализация подсказок поиска
  initSearchHints();
  initSearchOverlay();

  render();

  if (refs.products) {
    refs.products.classList.remove('is-hydrating');
  }
}

initListRowTouchHighlight();
initAjaxGallery();

initFloatingCartScroll();

initDrawer();

initHoldRepeat();

initQtyKeyboard();

document.addEventListener('submit', (e: Event) => {
  const form = e.target as HTMLFormElement;
  if (!form) return;

  if (form.dataset.actionSubmit === 'callback-form') {
    e.preventDefault();
    const phone = (form as any).phone?.value?.trim();
    if (!phone) return;
    const btn = form.querySelector('button[type="submit"]');
    if (btn) (btn as HTMLButtonElement).disabled = true;
    const callbackUrl = (getConfig('basePath', '/i') ?? '/i') + '/callback';
    void fetch(callbackUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'phone=' + encodeURIComponent(phone),
    })
      .then(r => r.ok ? showToast('Заявка принята, перезвоним!') : Promise.reject(new Error('Request failed')))
      .catch(() => showFlash('Ошибка, попробуйте позже', 'error'))
      .finally(() => { if (btn) (btn as HTMLButtonElement).disabled = false; form.reset(); });
    return;
  }

  if (form.id !== 'b_logout' && form.id !== 'drawer_logout') return;

  e.preventDefault();
  e.stopPropagation();

  const userMenu = form.closest('.i-user-menu');
  if (userMenu) {
    const dropdown = userMenu.querySelector('.i-user-menu-dropdown');
    if (dropdown) (dropdown as HTMLElement).style.display = 'none';
  }

  void submitAuthForm('', 'b_logout');
}, true);

window.TorgiRTCart = { clear: clearCart };

window.TorgiRTAuth = { submit: submitAuthForm, validateLogin: validateLoginForm };

// D.4: регистрация Service Worker (per-tenant cache через /sw.php).
// `.catch` с console.warn, НЕ silent — red line «Healthy logging».
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.php', { scope: '/' })
      .then((reg) => {
        if (typeof console !== 'undefined') {
          console.info('[SW] registered:', reg.scope);
        }
      })
      .catch((err) => {
        if (typeof console !== 'undefined') {
          console.warn('[SW] registration failed:', err);
        }
      });
  });
}

void init();
